import { supabase } from '@/lib/supabase';

// Ported from creator-app services/roster.ts. Env via Vite; no native toast —
// callers in onboarding use { silent: true } and fall back gracefully.
const ROSTER_API_BASE = import.meta.env.VITE_ROSTER_API_BASE || '';
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL || '';
/** Brand-Dashboard worker origin (no trailing slash); empty disables the support AI assistant. */
export const BRAND_WORKER_URL = (import.meta.env.VITE_BRAND_WORKER_URL || '').replace(/\/+$/, '');
const REQUEST_TIMEOUT_MS = 30000;

export class RosterAPIError extends Error {
  constructor(message: string, public statusCode?: number, public data?: any) {
    super(message);
    this.name = 'RosterAPIError';
  }
}

async function fetchWithTimeout(url: string, options: RequestInit): Promise<Response> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  try {
    const response = await fetch(url, { ...options, signal: controller.signal });
    clearTimeout(timeoutId);
    return response;
  } catch (error: any) {
    clearTimeout(timeoutId);
    if (error.name === 'AbortError') {
      throw new RosterAPIError(`Request timeout after ${REQUEST_TIMEOUT_MS / 1000}s`, 408);
    }
    throw error;
  }
}

async function handleResponse(response: Response): Promise<any> {
  let data: any = {};
  try {
    data = await response.json();
  } catch {}

  if (response.status === 401) throw new RosterAPIError(data.message || data.error || 'Unauthorized', 401);
  if (!response.ok) throw new RosterAPIError(data.message || data.error || 'Something went wrong', response.status, data);
  return data;
}

type RosterRequestOptions = { silent?: boolean };

async function rosterRequest<T = any>(
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH',
  path: string,
  body?: Record<string, any>,
  options?: RosterRequestOptions,
): Promise<T> {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;
    if (!token) throw new RosterAPIError('No active session — user must be logged in', 401);

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    };

    // Absolute URLs (the Brand-Dashboard worker) are used as-is; relative paths
    // still resolve against the legacy roster server.
    const url = /^https?:\/\//.test(path) ? path : `${ROSTER_API_BASE}${path}`;
    const response = await fetchWithTimeout(url, {
      method,
      headers,
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    });
    return await handleResponse(response);
  } catch (error: any) {
    if (!options?.silent && import.meta.env.DEV) console.warn('[roster] request failed:', error?.message);
    throw error;
  }
}

export const rosterApi = {
  // /open/* roster import — DISABLED, same as creator-app: onboarding no longer
  // imports creator data from the old roster (external /api server is being
  // shut down).
  // checkEmail(email: string, options?: RosterRequestOptions) {
  //   return rosterRequest('GET', '/open/check-email?email=' + email, undefined, options);
  // },
  // getCreatorData(payload: { email?: string; username?: string }, options?: RosterRequestOptions) {
  //   const params = new URLSearchParams();
  //   if (payload.email) params.set('email', payload.email);
  //   if (payload.username) params.set('username', payload.username);
  //   return rosterRequest('GET', `/open/tiktok-creator?${params.toString()}`, undefined, options);
  // },

};
